/**
 * Module Source Parser
 *
 * Pure TypeScript implementation for parsing Terraform module source strings.
 * Copied from backend (terraform-module-scaffolder-backend/router.ts) for
 * frontend use to avoid backend dependency.
 *
 * Supported formats:
 * - Registry: namespace/name/provider (e.g., hashicorp/consul/aws)
 * - Private Registry: app.terraform.io/org/name/provider
 * - GitHub: github.com/owner/repo
 * - Git: git::https://... or git@github.com:...
 * - Local: ./path or ../path
 */

/**
 * Parsed module source information
 */
export interface ModuleSourceInfo {
  /** Type of module source */
  type: 'registry' | 'github' | 'git' | 'local' | 'unknown';
  /** Registry type (public/private) - only for registry sources */
  registryType?: 'public' | 'private';
  /** Module namespace (registry) or owner (GitHub) */
  namespace?: string;
  /** Module name */
  name?: string;
  /** Provider (e.g., 'aws', 'google') */
  provider?: string;
  /** Module version from source URL */
  version?: string;
  /** TFC organization (for private registry) */
  organization?: string;
  /** GitHub repository owner */
  owner?: string;
  /** GitHub repository name */
  repo?: string;
  /** Local path (for local sources) */
  path?: string;
  /** Git ref (branch/tag) from source URL */
  ref?: string;
  /** Subpath within repo (//subdir) */
  subdir?: string;
}

/**
 * Helper to extract ref and subdir from URL
 */
function extractRefAndSubdir(url: string): { ref?: string; subdir?: string } {
  const result: { ref?: string; subdir?: string } = {};

  // Extract ref from ?ref=xxx query parameter
  const refMatch = url.match(/[?&]ref=([^&]+)/);
  if (refMatch) {
    result.ref = refMatch[1];
  }

  // Extract subdir from //subdir path
  const subdirMatch = url.match(/\/\/([^?]+)/);
  if (subdirMatch) {
    result.subdir = subdirMatch[1];
  }

  return result;
}

/**
 * Parse a Terraform module source string to extract registry information
 *
 * @param source - Module source string from Terraform configuration
 * @returns Parsed module source information
 *
 * @example
 * // Public registry
 * parseModuleSource('hashicorp/consul/aws')
 * // => { type: 'registry', registryType: 'public', namespace: 'hashicorp', name: 'consul', provider: 'aws' }
 *
 * @example
 * // Private TFC registry
 * parseModuleSource('app.terraform.io/my-org/vpc/aws')
 * // => { type: 'registry', registryType: 'private', organization: 'my-org', namespace: 'my-org', name: 'vpc', provider: 'aws' }
 *
 * @example
 * // GitHub source
 * parseModuleSource('github.com/terraform-aws-modules/terraform-aws-vpc//modules/vpc-endpoints?ref=v5.0.0')
 * // => { type: 'github', owner: 'terraform-aws-modules', repo: 'terraform-aws-vpc', subdir: 'modules/vpc-endpoints', ref: 'v5.0.0' }
 */
export function parseModuleSource(source: string): ModuleSourceInfo {
  const trimmedSource = source.trim();

  // Local paths
  if (trimmedSource.startsWith('./') || trimmedSource.startsWith('../')) {
    return { type: 'local', path: trimmedSource };
  }

  // Git URLs (git::https://... or git@github.com:...)
  if (
    trimmedSource.startsWith('git::') ||
    trimmedSource.startsWith('git@') ||
    trimmedSource.includes('.git')
  ) {
    // Extract GitHub info if it's a GitHub git URL
    // Patterns:
    // - git::https://github.com/owner/repo.git
    // - git::https://github.com/owner/repo.git//subdir?ref=v1.0.0
    // - git@github.com:owner/repo.git
    const githubMatch = trimmedSource.match(
      /github\.com[:/]([^/]+)\/([^/.?]+)(?:\.git)?/,
    );
    if (githubMatch) {
      const extras = extractRefAndSubdir(trimmedSource);
      return {
        type: 'github',
        owner: githubMatch[1],
        repo: githubMatch[2].replace(/\.git$/, ''),
        ...extras,
      };
    }
    return { type: 'git' };
  }

  // Private registry (app.terraform.io)
  const privateRegistryMatch = trimmedSource.match(
    /^app\.terraform\.io\/([^/]+)\/([^/]+)\/([^/?]+)(?:\/\/.*)?$/,
  );
  if (privateRegistryMatch) {
    return {
      type: 'registry',
      registryType: 'private',
      organization: privateRegistryMatch[1],
      namespace: privateRegistryMatch[1],
      name: privateRegistryMatch[2],
      provider: privateRegistryMatch[3],
    };
  }

  // GitHub shorthand (github.com/owner/repo or github.com/owner/repo//subdir?ref=xxx)
  const githubMatch = trimmedSource.match(
    /^github\.com\/([^/]+)\/([^/?]+)(?:\/\/([^?]+))?(?:\?ref=([^&]+))?/,
  );
  if (githubMatch) {
    return {
      type: 'github',
      owner: githubMatch[1],
      repo: githubMatch[2],
      subdir: githubMatch[3],
      ref: githubMatch[4],
    };
  }

  // Public registry (namespace/name/provider format)
  // This must come after GitHub checks since it's a generic 3-part pattern
  const publicRegistryMatch = trimmedSource.match(
    /^([^/]+)\/([^/]+)\/([^/?]+)(?:\/\/.*)?$/,
  );
  if (publicRegistryMatch) {
    return {
      type: 'registry',
      registryType: 'public',
      namespace: publicRegistryMatch[1],
      name: publicRegistryMatch[2],
      provider: publicRegistryMatch[3],
    };
  }

  return { type: 'unknown' };
}

/**
 * Check if a module source is from the public Terraform registry
 */
export function isPublicRegistry(source: ModuleSourceInfo): boolean {
  return source.type === 'registry' && source.registryType === 'public';
}

/**
 * Check if a module source is from a private TFC registry
 */
export function isPrivateRegistry(source: ModuleSourceInfo): boolean {
  return source.type === 'registry' && source.registryType === 'private';
}

/**
 * Check if a module source is from GitHub
 */
export function isGitHubSource(source: ModuleSourceInfo): boolean {
  return source.type === 'github' && !!source.owner && !!source.repo;
}

/**
 * Check if a module source is a local path
 */
export function isLocalSource(source: ModuleSourceInfo): boolean {
  return source.type === 'local';
}

/**
 * Build the public Terraform registry API URL for a module
 */
export function buildPublicRegistryUrl(
  source: ModuleSourceInfo,
  version?: string,
): string | null {
  if (!isPublicRegistry(source)) {
    return null;
  }

  const baseUrl = 'https://registry.terraform.io/v1/modules';
  const modulePath = `${source.namespace}/${source.name}/${source.provider}`;

  if (version) {
    return `${baseUrl}/${modulePath}/${version}`;
  }

  return `${baseUrl}/${modulePath}`;
}

/**
 * Build the GitHub API URL for fetching variables.tf
 */
export function buildGitHubVariablesUrl(
  source: ModuleSourceInfo,
  ref?: string,
): string | null {
  if (!isGitHubSource(source)) {
    return null;
  }

  const gitRef = ref || source.ref || 'main';
  const basePath = source.subdir ? `${source.subdir}/` : '';
  const variablesPath = `${basePath}variables.tf`;

  return `https://api.github.com/repos/${source.owner}/${source.repo}/contents/${variablesPath}?ref=${gitRef}`;
}
